import React, { useEffect, useState } from 'react';
import TailwindModal from './Tailwind/TailwindModal';
import apiService from '../services/ApiService';
import snakeToPascal from '../tools';

interface SaveToLibraryModalProps {
    isOpen: boolean;
    onClose: () => void;
    itemId: number;
    itemName: string;
    setContextSnackBarContent: (content: any) => void;
}

const IS_DEV = process.env.ENVIRONMENT === 'dev';

interface ExternalRef {
    model_data_id: number;
    model_data_name: string;
    model_data_type: string;
    field_name: string;
    field_display_name: string;
    field_type: string;
    kind: 'cleared' | 'detached';
}

export default function SaveToLibraryModal({
    isOpen,
    onClose,
    itemId,
    itemName,
    setContextSnackBarContent,
}: SaveToLibraryModalProps) {
    const [name, setName] = useState(itemName);
    const [description, setDescription] = useState('');
    const [includeChildren, setIncludeChildren] = useState(true);
    const [saveAsGlobal, setSaveAsGlobal] = useState(false);
    const [loading, setLoading] = useState(false);

    const [externalRefs, setExternalRefs] = useState<ExternalRef[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Re-run the dry-run whenever the user opens the modal or toggles
    // include_children — the saved subtree (and therefore the set of external
    // refs) shifts based on that flag.
    useEffect(() => {
        if (!isOpen || !itemId) return undefined;
        let cancelled = false;
        setPreviewLoading(true);
        apiService.previewSaveToLibrary(itemId, includeChildren).then(async response => {
            if (cancelled) return;
            if (response && response.ok) {
                const data = await response.json();
                setExternalRefs(data.external_refs ?? []);
            } else {
                setExternalRefs([]);
            }
            setPreviewLoading(false);
        }).catch(() => {
            if (!cancelled) {
                setExternalRefs([]);
                setPreviewLoading(false);
            }
        });
        return () => { cancelled = true; };
    }, [isOpen, itemId, includeChildren]);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setLoading(true);
        try {
            const response = saveAsGlobal
                ? await apiService.saveGlobalLibraryBlock(name, description || null, itemId, includeChildren)
                : await apiService.saveToLibrary(name, description || null, itemId, includeChildren);
            if (response && response.ok) {
                setContextSnackBarContent({
                    open: true,
                    severity: 'success',
                    message: saveAsGlobal ? 'Saved as Peerception block' : 'Saved to Library',
                });
                onClose();
            } else {
                setContextSnackBarContent({ open: true, severity: 'warning', message: 'Failed to save to library' });
            }
        } catch (e) {
            setContextSnackBarContent({ open: true, severity: 'warning', message: 'Failed to save to library' });
        }
        setLoading(false);
    };

    // Group external refs by source model_data so the preview lists "Asset:
    // country_fiscality, indexation" rather than a flat row per field.
    const refsByMd = externalRefs.reduce<Record<number, { name: string; type: string; refs: ExternalRef[] }>>((acc, ref) => {
        const key = ref.model_data_id;
        if (!acc[key]) {
            acc[key] = { name: ref.model_data_name || snakeToPascal(ref.model_data_type), type: ref.model_data_type, refs: [] };
        }
        acc[key].refs.push(ref);
        return acc;
    }, {});
    const groupedMds = Object.entries(refsByMd);

    const totalCleared = externalRefs.filter(r => r.kind === 'cleared').length;
    const totalDetached = externalRefs.filter(r => r.kind === 'detached').length;

    return (
        <TailwindModal
            isOpen={isOpen}
            onClose={onClose}
            title="Save to Library"
            size="md"
        >
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Library item name"
                        autoFocus
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Describe this building block..."
                        rows={3}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="includeChildren"
                        checked={includeChildren}
                        onChange={(e) => setIncludeChildren(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="includeChildren" className="text-sm text-gray-700">
                        Include children
                    </label>
                </div>

                {/* Preview of references that will be cleared / detached. */}
                {previewLoading ? (
                    <div className="text-xs text-slate-400 italic">Checking external references…</div>
                ) : externalRefs.length > 0 ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                        <div className="flex items-start gap-2">
                            <span className="material-symbols-outlined text-[18px] text-amber-700 mt-0.5">warning</span>
                            <div className="flex-1 min-w-0">
                                <div className="text-[12.5px] font-semibold text-amber-900">
                                    {totalCleared > 0 && `${totalCleared} reference${totalCleared === 1 ? '' : 's'} will be cleared`}
                                    {totalCleared > 0 && totalDetached > 0 && ' · '}
                                    {totalDetached > 0 && `${totalDetached} timing${totalDetached === 1 ? '' : 's'} will be detached`}
                                </div>
                                <p className="text-[11.5px] text-amber-800 mt-0.5">
                                    Library blocks are self-contained, so links to anything outside the saved subtree
                                    {includeChildren ? ' (root + all children)' : ' (root only)'} can't follow.
                                    The block itself saves fine — you'll just need to re-bind these fields when applying it to a model.
                                </p>
                            </div>
                        </div>
                        <div className="space-y-1.5 max-h-[180px] overflow-y-auto pl-7">
                            {groupedMds.map(([id, group]) => (
                                <div key={id} className="text-[11.5px]">
                                    <div className="font-medium text-amber-900 truncate">
                                        {group.name}
                                        <span className="text-amber-700 font-normal ml-1">
                                            · {snakeToPascal(group.type)}
                                        </span>
                                    </div>
                                    <ul className="ml-3 mt-0.5 space-y-0.5">
                                        {group.refs.map((ref, i) => (
                                            <li key={i} className="text-amber-800 flex items-baseline gap-1.5">
                                                <span className="text-amber-500">·</span>
                                                <span className="truncate">{ref.field_display_name || snakeToPascal(ref.field_name)}</span>
                                                <span className={
                                                    'text-[10px] uppercase tracking-wider shrink-0 ' +
                                                    (ref.kind === 'detached' ? 'text-amber-600' : 'text-amber-700')
                                                }>
                                                    {ref.kind}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="text-[11.5px] text-emerald-700 italic flex items-center gap-1">
                        <span className="material-symbols-outlined text-[16px]">check_circle</span>
                        No external references — this block is self-contained.
                    </div>
                )}

                {IS_DEV && (
                    <div className="flex items-center gap-2 p-2.5 rounded-md bg-blue-50 border border-blue-100">
                        <input
                            type="checkbox"
                            id="saveAsGlobal"
                            checked={saveAsGlobal}
                            onChange={(e) => setSaveAsGlobal(e.target.checked)}
                            className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="saveAsGlobal" className="text-sm text-blue-900">
                            Save as Peerception block <span className="text-xs text-blue-700">(dev only — writes to git-versioned JSON)</span>
                        </label>
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading || !name.trim()}
                        className="px-4 py-2 text-sm text-white bg-black rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : saveAsGlobal ? 'Save as Peerception block' : 'Save to Library'}
                    </button>
                </div>
            </div>
        </TailwindModal>
    );
}
